// Modal fuer manuelle Coaching-Aktivierung im Admin-User-Drawer.

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Sparkles, X } from 'lucide-react';
import type { AdminUserRow } from '@/lib/admin-users-api';
import type { CoachingPlan } from '@/lib/coaching-api';

interface CoachingActivationDialogProps {
  user: AdminUserRow;
  onClose: () => void;
  onConfirm: (input: { plan: CoachingPlan; period_end: string; notes?: string }) => Promise<void>;
}

const PLAN_MONTHS: Record<CoachingPlan, number> = {
  quarterly: 3,
  half_yearly: 6,
  yearly: 12,
};

const PLAN_LABEL: Record<CoachingPlan, string> = {
  quarterly: 'Quarterly (3 Monate)',
  half_yearly: 'Half-yearly (6 Monate)',
  yearly: 'Yearly (12 Monate)',
};

export function CoachingActivationDialog({ user, onClose, onConfirm }: CoachingActivationDialogProps) {
  const [plan, setPlan] = useState<CoachingPlan>('quarterly');
  const [periodEnd, setPeriodEnd] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + PLAN_MONTHS.quarterly);
    return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  function handlePlanChange(p: CoachingPlan) {
    setPlan(p);
    const d = new Date();
    d.setMonth(d.getMonth() + PLAN_MONTHS[p]);
    setPeriodEnd(d.toISOString().slice(0, 10));
  }

  async function submit() {
    setSaving(true);
    try {
      await onConfirm({ plan, period_end: periodEnd, notes: notes.trim() || undefined });
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong w-[min(480px,92vw)] rounded-2xl border border-amber-500/30 p-5"
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="size-5 text-amber-300" />
            <h2 className="text-base font-bold text-amber-200">Coaching manuell aktivieren</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-neutral-500 hover:bg-white/5 hover:text-neutral-200"
          >
            <X className="size-4" />
          </button>
        </div>
        <p className="mb-4 text-[12px] text-neutral-400">
          Aktivierung fuer <span className="font-mono text-neutral-100">{user.email}</span> — z.B. bei
          Gumroad-Migration. Bei Stripe-Abos wird das ueber den Stripe-Webhook gesetzt, nicht hier.
        </p>

        <label className="block text-[11px] uppercase tracking-wider text-neutral-500">Plan</label>
        <div className="mt-1 mb-3 grid grid-cols-3 gap-1.5">
          {(Object.keys(PLAN_LABEL) as CoachingPlan[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => handlePlanChange(p)}
              className={`rounded-md border px-2 py-1.5 text-[11px] font-medium ${
                plan === p
                  ? 'border-amber-500/50 bg-amber-500/15 text-amber-200'
                  : 'border-white/10 bg-white/[0.02] text-neutral-400 hover:border-amber-500/30'
              }`}
            >
              {PLAN_LABEL[p]}
            </button>
          ))}
        </div>

        <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
          Laeuft bis
        </label>
        <input
          type="date"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
          className="mb-3 mt-1 w-full rounded-md border border-white/10 bg-neutral-900/60 px-3 py-1.5 text-[12px] text-neutral-100 outline-none focus:border-amber-500/40"
        />

        <label className="block text-[11px] uppercase tracking-wider text-neutral-500">
          Notizen (optional)
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="z.B. Gumroad-Migration"
          className="mb-3 mt-1 w-full rounded-md border border-white/10 bg-neutral-900/60 px-3 py-1.5 text-[12px] text-neutral-100 outline-none focus:border-amber-500/40"
        />

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-[12px] text-neutral-400 hover:text-neutral-200"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40"
          >
            {saving ? <Loader2 className="size-3 animate-spin" /> : <Sparkles className="size-3" />}
            Aktivieren
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
